import { StrictMode, useEffect, useRef, useState } from "react";
import { createRoot } from "react-dom/client";
import { ClerkProvider, useAuth } from "@clerk/react";
import { QueryClientProvider } from "@tanstack/react-query";
import { RouterProvider } from "@tanstack/react-router";
import "@fontsource/figtree/400.css";
import "@fontsource/figtree/500.css";
import "@fontsource/figtree/600.css";
import "@fontsource/figtree/700.css";
import "@fontsource/jetbrains-mono/400.css";
import "@fontsource/jetbrains-mono/500.css";
import "./estilos.css";
import { usarToken } from "./api";
import { queryClient } from "./consultas";
import { router } from "./rotas";

/**
 * A ordem de uma entrada, e por que ela não se monta aos pedaços:
 *
 *   1. o HTML já traz a abertura, desenhada no primeiro quadro;
 *   2. o Clerk carrega e diz quem é;
 *   3. o roteador busca TUDO o que a primeira tela precisa, em paralelo;
 *   4. só então a abertura sai — e a tela entra inteira, de uma vez.
 *
 * Depois disso nada mais passa por aqui: trocar de tela é assunto do roteador.
 */
function App() {
  const { isLoaded, isSignedIn, orgId, getToken } = useAuth();
  const [pronto, setPronto] = useState(false);
  const primeira = useRef(true);

  useEffect(() => {
    if (!isLoaded) return;
    usarToken(() => getToken());
    const auth = { entrou: Boolean(isSignedIn), clinica: orgId ?? null };
    router.update({ ...router.options, context: { auth } });

    // Saiu da conta: nada do que estava guardado fica na memória para quem
    // sentar na frente do computador depois.
    if (!isSignedIn) queryClient.clear();

    if (primeira.current) {
      primeira.current = false;
      void router.load().finally(() => setPronto(true));
    } else {
      void router.invalidate();
    }
  }, [isLoaded, isSignedIn, orgId, getToken]);

  if (!pronto) return <Abertura />;
  return <RouterProvider router={router} />;
}

/** A mesma abertura do index.html — a troca entre as duas é invisível. */
function Abertura() {
  return (
    <div className="abertura">
      <span>
        <i />
        LummiChat
      </span>
    </div>
  );
}

const chave = import.meta.env.CLERK_PUBLISHABLE_KEY;
if (!chave) throw new Error("Falta CLERK_PUBLISHABLE_KEY no .env.");

createRoot(document.getElementById("root")!).render(
  <StrictMode>
    <ClerkProvider
      publishableKey={chave}
      signInUrl="/sign-in"
      signUpUrl="/sign-up"
      afterSignOutUrl="/sign-in"
      signInFallbackRedirectUrl="/conversas"
      signUpFallbackRedirectUrl="/conversas"
      routerPush={(to) => router.navigate({ to })}
      routerReplace={(to) => router.navigate({ to, replace: true })}
    >
      <QueryClientProvider client={queryClient}>
        <App />
      </QueryClientProvider>
    </ClerkProvider>
  </StrictMode>,
);
